/*
 * Wrap a component along with a media query (or queries). If a media query
 * changes, re-render the component. Pass in an object of strings, and the
 * wrapped component will get a `matchesMediaQuery` prop with an object of
 * booleans.
 */

import React, { Component } from 'react';
import _ from 'lodash';

import hocify from 'app/common/components/hocify';

interface MediaQueryList {
  matches: boolean;
  addListener: (listener: (event: MediaQueryListEvent) => void) => void;
  removeListener: (listener: (event: MediaQueryListEvent) => void) => void;
}

type MediaQueryLists = {
  [key: string]: MediaQueryList;
};

interface MatchesMediaQueryProps {
  Component: React.ComponentType<any>;
  mediaQueries: { [key: string]: string };
  [key: string]: any;
}

interface MatchesMediaQueryState {
  matchesMediaQuery: { [key: string]: boolean };
}

export class MatchesMediaQuery extends Component<
  MatchesMediaQueryProps,
  MatchesMediaQueryState
> {
  mediaQueryLists: MediaQueryLists | null = null;

  state: MatchesMediaQueryState = {
    matchesMediaQuery: {},
  };

  UNSAFE_componentWillMount() {
    this.configureMediaQueries(this.props.mediaQueries);
  }

  UNSAFE_componentWillReceiveProps(nextProps: MatchesMediaQueryProps) {
    if (!_.isEqual(this.props.mediaQueries, nextProps.mediaQueries)) {
      this.configureMediaQueries(nextProps.mediaQueries);
    }
  }

  componentWillUnmount() {
    this.bindMediaQueryListeners(false);
    this.mediaQueryLists = null;
  }

  configureMediaQueries(mediaQueries: { [key: string]: string }) {
    this.bindMediaQueryListeners(false);
    this.buildMediaQueryLists(mediaQueries);
    this.bindMediaQueryListeners(true);
    this.setMatchesState();
  }

  buildMediaQueryLists(mediaQueries: { [key: string]: string }) {
    this.mediaQueryLists = Object.keys(mediaQueries).reduce(
      (lists, key) => ({
        [key]: window.matchMedia(mediaQueries[key]) as MediaQueryList,
        ...lists,
      }),
      {} as MediaQueryLists
    );
  }

  bindMediaQueryListeners(shouldBind: boolean) {
    const { mediaQueryLists } = this;
    const method = shouldBind ? 'addListener' : 'removeListener';
    Object.keys(mediaQueryLists || {}).forEach(key => {
      const mediaQuery = _.get(mediaQueryLists, key);
      const methodFn = _.get(mediaQuery, method);
      if (mediaQuery && methodFn) {
        methodFn.call(mediaQuery, this.onChangeMediaQuery);
      }
    });
  }

  onChangeMediaQuery = () => {
    this.setMatchesState();
  };

  setMatchesState() {
    const { mediaQueryLists } = this;
    if (!mediaQueryLists) {
      return;
    }

    const matchesMediaQuery = Object.keys(mediaQueryLists).reduce(
      (result, key) => ({
        [key]: mediaQueryLists[key].matches,
        ...result,
      }),
      {} as { [key: string]: boolean }
    );
    this.setState({ matchesMediaQuery });
  }

  render() {
    const { Component: WrappedComponent, ...props } = this.props;
    const childProps = _.omit(props, 'mediaQueries');
    return <WrappedComponent {...childProps} {...this.state} />;
  }
}

const matchesMediaQuery = (mediaQueries: { [key: string]: string }) =>
  hocify(MatchesMediaQuery, { mediaQueries });

export default matchesMediaQuery;
